import { makeAutoObservable, runInAction } from 'mobx';
import { IConfiguratorCatalogProductApiOptions, IContactUsFeatureDto } from '@canvas-logic/clp-api-types';
import { FetcherApi } from '@canvas-logic/clp-sdk';

export class ContactUsService {
  schema: IContactUsFeatureDto | null = null;

  requestStatus: 'pending' | 'success' | 'error' | 'idle' = 'idle';

  constructor(private readonly params: IConfiguratorCatalogProductApiOptions, private readonly fetcher: FetcherApi) {
    makeAutoObservable(
      this,
      {},
      {
        autoBind: true,
      },
    );

    this.getSchema();
  }

  async getSchema() {
    const schema = await this.fetcher.getContactUsFeatureSchema(this.params);

    runInAction(() => {
      this.schema = schema;
    });
  }

  isEnabled(): boolean {
    if (!this.schema?.enabled) {
      return false;
    }

    // CP-1332: `enabled` folds in whether the catalog has recipients — the API no longer
    // discloses them to the configurator.
    const configuration = this.schema?.configuration;
    if (!configuration) {
      return false;
    }

    if (!this.hasAnyPrivacyLink()) {
      return false;
    }

    if (!this.hasActiveFields()) {
      return false;
    }

    return true;
  }

  private hasAnyPrivacyLink(): boolean {
    const configuration = this.schema?.configuration;
    if (!configuration) {
      return false;
    }
    if (configuration.privacyConditionsLink) {
      return true;
    }
    if (!configuration.regionSpecificLinksEnabled) {
      return false;
    }
    const links = configuration.privacyConditionsLinks ?? {};
    return Object.values(links).some((value) => value && value.length > 0);
  }

  /**
   * @param routingKey the `routing_key` identifier used for Advanced Routing. Resolved by the
   *   caller from the URL / iframe host page so this service stays decoupled from embedding.
   */
  async requestQuote(formData: Record<string, string>, routingKey?: string) {
    try {
      this.requestStatus = 'pending';
      await this.fetcher.requestQuote(this.params, formData, routingKey);

      runInAction(() => {
        this.requestStatus = 'success';
      });
    } catch {
      runInAction(() => {
        this.requestStatus = 'error';
      });
    }
  }

  resetStatus() {
    this.requestStatus = 'idle';
  }

  private hasActiveFields(): boolean {
    const configuration = this.schema?.configuration;
    if (!configuration) {
      return false;
    }

    return configuration.fieldGroups.flatMap((group) => group.fields).some((field) => !field.hidden);
  }
}
